import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import {
  Badge,
  Button,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import moment from "moment";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { fetchApi } from "redux/action/api_tbeams";
import { useToasts } from "helper/ToastAdapter";

const PAGE_SIZES = [25, 50, 100, 200];

const OUTCOME_STYLES = {
  success: { backgroundColor: "#e8f5e9", color: "#2e7d32", label: "Success" },
  failed: { backgroundColor: "#ffebee", color: "#c62828", label: "Failed" },
};

const categoryColor = (category = "") => {
  const palette = {
    Releases: "#1976d2",
    Budget: "#6a1b9a",
    Payments: "#00695c",
    "Cash Office": "#00838f",
    Memos: "#ef6c00",
    Auth: "#455a64",
    "Auth & Users": "#455a64",
    Users: "#37474f",
    Setup: "#5d4037",
    "Budget & Setup": "#6a1b9a",
    Attachments: "#ad1457",
    Contractors: "#4e342e",
    Reports: "#0277bd",
    Revenue: "#827717",
    Virement: "#283593",
    "Planning Reserve": "#283593",
    TSA: "#00838f",
    Other: "#616161",
  };
  return palette[category] || "#616161";
};

const prettyPayload = (payload) => {
  if (!payload) return "No request details were recorded.";
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch (err) {
    return payload;
  }
};

export default function AuditTrail() {
  const { addToast } = useToasts();

  // useToasts() builds a new addToast on every render. Using it directly inside
  // the loaders would change their identity on each render and re-run the
  // effects that depend on them, refetching in an endless loop, so keep it in a
  // ref that the loaders read instead.
  const addToastRef = useRef(addToast);
  useEffect(() => {
    addToastRef.current = addToast;
  }, [addToast]);

  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  // Draft filters are only applied when the user asks for them, so typing in
  // the search box does not fire a request per keystroke.
  const [draft, setDraft] = useState({
    search: "",
    category: "",
    actor: "",
    method: "",
    outcome: "",
    date_from: "",
    date_to: "",
  });
  const [applied, setApplied] = useState(draft);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(applied).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) params.set(key, value);
    });
    params.set("page", page);
    params.set("limit", limit);
    return params.toString();
  }, [applied, page, limit]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchApi(`audit-log?${queryString}`);
      if (data?.success) {
        setLogs(data.logs || []);
        setPagination(data.pagination || { total: 0, page: 1, limit, totalPages: 1 });
      } else {
        setLogs([]);
        addToastRef.current(data?.message || data?.error || "Could not load the audit trail", {
          appearance: "error",
          autoDismiss: true,
          autoDismissTimeout: 6000,
        });
      }
    } catch (err) {
      console.log(err);
      addToastRef.current("Could not load the audit trail", {
        appearance: "error",
        autoDismiss: true,
      });
    } finally {
      setLoading(false);
    }
  }, [queryString, limit]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await fetchApi("audit-log/stats");
      if (data?.success) setStats(data);
    } catch (err) {
      console.log(err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleDraft = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const applyFilters = () => {
    setPage(1);
    setApplied(draft);
  };

  const resetFilters = () => {
    const empty = {
      search: "",
      category: "",
      actor: "",
      method: "",
      outcome: "",
      date_from: "",
      date_to: "",
    };
    setDraft(empty);
    setApplied(empty);
    setPage(1);
  };

  const refresh = () => {
    loadLogs();
    loadStats();
  };

  const totals = stats?.totals || {};
  const statCards = [
    { label: "Today", value: totals.today, tone: "#2e7d32" },
    { label: "Last 7 days", value: totals.last_7_days, tone: "#1976d2" },
    { label: "Last 30 days", value: totals.last_30_days, tone: "#6a1b9a" },
    { label: "All time", value: totals.total, tone: "#00695c" },
    { label: "Failed attempts", value: totals.failed, tone: "#c62828" },
  ];

  const detailModal = (
    <Modal isOpen={!!selected} toggle={() => setSelected(null)} size="lg" scrollable>
      <ModalHeader toggle={() => setSelected(null)} style={{ backgroundColor: "#f8f9fa" }}>
        <span style={{ fontWeight: 600 }}>Audit entry #{selected?.id}</span>
      </ModalHeader>
      <ModalBody>
        {selected && (
          <>
            <Row className="mb-2">
              <Col md={6}>
                <strong>Officer:</strong> {selected.actor_name}
                {selected.actor_username ? ` (${selected.actor_username})` : ""}
                <br />
                <strong>Role:</strong> {selected.actor_role || "—"}
              </Col>
              <Col md={6}>
                <strong>When:</strong> {moment(selected.created_at).format("MMM DD, YYYY hh:mm:ss A")}
                <br />
                <strong>Duration:</strong> {selected.duration_ms} ms
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={12}>
                <strong>Action:</strong> {selected.action}
                <br />
                <strong>Category:</strong> {selected.category}
                <br />
                <strong>Reference:</strong> {selected.entity_ref || "—"}
                <br />
                <strong>Request:</strong> {selected.method} {selected.route}
                <br />
                <strong>Outcome:</strong> HTTP {selected.status_code}{" "}
                {selected.success ? "(success)" : "(failed or rejected)"}
                <br />
                <strong>IP:</strong> {selected.ip_address || "—"}
              </Col>
            </Row>
            <MDTypography variant="button" fontWeight="bold">
              Request data
            </MDTypography>
            <pre
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: 6,
                padding: 12,
                fontSize: 12,
                maxHeight: 320,
                overflow: "auto",
              }}
            >
              {prettyPayload(selected.payload)}
            </pre>
            <MDTypography variant="caption" color="text">
              Passwords, tokens and other secrets are removed before an entry is stored.
              <br />
              Client: {selected.user_agent || "—"}
            </MDTypography>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setSelected(null)}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );

  return (
    <DashboardLayout>
      <MDBox pt={2} pb={3} px={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
              >
                <MDTypography variant="h5" color="white">
                  Audit Trail
                </MDTypography>
                <MDTypography variant="button" color="white">
                  Every action recorded across the system
                </MDTypography>
              </MDBox>

              <MDBox p={3}>
                {/* Headline numbers */}
                <div className="d-flex flex-wrap mb-3" style={{ gap: 12 }}>
                  {statCards.map((card) => (
                    <MDBox
                      key={card.label}
                      p={2}
                      borderRadius="lg"
                      border="1px solid #e9ecef"
                      style={{ backgroundColor: "#fff", flex: "1 1 170px", minWidth: 150 }}
                    >
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        {card.label}
                      </MDTypography>
                      <MDTypography variant="h5" style={{ color: card.tone, fontWeight: 700 }}>
                        {statsLoading ? "—" : Number(card.value || 0).toLocaleString()}
                      </MDTypography>
                    </MDBox>
                  ))}
                </div>

                {/* Filters */}
                <Row className="mb-3">
                  <Col md={3}>
                    <label className="font-weight-bold" style={{ fontSize: 12 }}>
                      Search
                    </label>
                    <Input
                      bsSize="sm"
                      placeholder="Officer, action, reference or route"
                      value={draft.search}
                      onChange={(e) => handleDraft("search", e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                    />
                  </Col>
                  <Col md={2}>
                    <label className="font-weight-bold" style={{ fontSize: 12 }}>
                      Category
                    </label>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={draft.category}
                      onChange={(e) => handleDraft("category", e.target.value)}
                    >
                      <option value="">All</option>
                      {(stats?.filters?.categories || []).map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <label className="font-weight-bold" style={{ fontSize: 12 }}>
                      Officer
                    </label>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={draft.actor}
                      onChange={(e) => handleDraft("actor", e.target.value)}
                    >
                      <option value="">All officers</option>
                      {(stats?.filters?.actors || []).map((actor) => (
                        <option key={actor.actor_username} value={actor.actor_username}>
                          {actor.actor_name || actor.actor_username}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={1}>
                    <label className="font-weight-bold" style={{ fontSize: 12 }}>
                      Method
                    </label>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={draft.method}
                      onChange={(e) => handleDraft("method", e.target.value)}
                    >
                      <option value="">All</option>
                      {(stats?.filters?.methods || []).map((method) => (
                        <option key={method} value={method}>
                          {method}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <label className="font-weight-bold" style={{ fontSize: 12 }}>
                      Outcome
                    </label>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={draft.outcome}
                      onChange={(e) => handleDraft("outcome", e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="success">Successful</option>
                      <option value="failed">Failed</option>
                    </Input>
                  </Col>
                  <Col md={2}>
                    <label className="font-weight-bold" style={{ fontSize: 12 }}>
                      From
                    </label>
                    <Input
                      type="date"
                      bsSize="sm"
                      value={draft.date_from}
                      onChange={(e) => handleDraft("date_from", e.target.value)}
                    />
                  </Col>
                  <Col md={2} className="mt-2">
                    <label className="font-weight-bold" style={{ fontSize: 12 }}>
                      To
                    </label>
                    <Input
                      type="date"
                      bsSize="sm"
                      value={draft.date_to}
                      onChange={(e) => handleDraft("date_to", e.target.value)}
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12} className="d-flex flex-wrap" style={{ gap: 8 }}>
                    <Button color="success" size="sm" onClick={applyFilters}>
                      Apply filters
                    </Button>
                    <Button color="secondary" size="sm" outline onClick={resetFilters}>
                      Reset
                    </Button>
                    <Button color="info" size="sm" outline onClick={refresh}>
                      Refresh
                    </Button>
                    <span className="ml-auto align-self-center" style={{ fontSize: 13, color: "#666" }}>
                      Showing {logs.length} of {Number(pagination.total || 0).toLocaleString()} recorded
                      actions
                    </span>
                  </Col>
                </Row>

                {/* Recent activity by category */}
                {!!(stats?.by_category || []).length && (
                  <Row className="mb-3">
                    <Col md={12}>
                      <MDTypography variant="caption" color="text" fontWeight="medium">
                        Last 30 days by category
                      </MDTypography>
                      <div className="d-flex flex-wrap" style={{ gap: 8, marginTop: 6 }}>
                        {stats.by_category.map((item) => (
                          <Badge
                            key={item.category}
                            pill
                            style={{
                              backgroundColor: categoryColor(item.category),
                              color: "#fff",
                              padding: "6px 10px",
                              fontWeight: 500,
                            }}
                          >
                            {item.category || "Other"}: {item.count}
                          </Badge>
                        ))}
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Trail table */}
                <div style={{ overflowX: "auto" }}>
                  <Table bordered hover responsive size="sm">
                    <thead style={{ backgroundColor: "#f8f9fa" }}>
                      <tr>
                        <th>When</th>
                        <th>Officer</th>
                        <th>Action</th>
                        <th>Category</th>
                        <th>Reference</th>
                        <th>Request</th>
                        <th>Outcome</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={8} className="text-center py-4">
                            <Spinner color="success" /> Loading audit trail…
                          </td>
                        </tr>
                      ) : logs.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-4" style={{ color: "#999" }}>
                            No audited actions match these filters yet.
                          </td>
                        </tr>
                      ) : (
                        logs.map((log) => {
                          const outcome = log.success ? OUTCOME_STYLES.success : OUTCOME_STYLES.failed;
                          return (
                            <tr key={log.id}>
                              <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                {moment(log.created_at).format("MMM DD, YYYY")}
                                <br />
                                <small style={{ color: "#666" }}>
                                  {moment(log.created_at).format("hh:mm:ss A")}
                                </small>
                              </td>
                              <td style={{ fontSize: 12 }}>
                                <div style={{ fontWeight: 500 }}>{log.actor_name}</div>
                                <small style={{ color: "#666" }}>
                                  {log.actor_username || "—"}
                                  {log.actor_role ? ` · ${log.actor_role}` : ""}
                                </small>
                              </td>
                              <td style={{ fontSize: 12, minWidth: 200 }}>{log.action}</td>
                              <td style={{ fontSize: 12 }}>
                                <Badge
                                  pill
                                  style={{
                                    backgroundColor: categoryColor(log.category),
                                    color: "#fff",
                                    fontWeight: 500,
                                  }}
                                >
                                  {log.category || "Other"}
                                </Badge>
                              </td>
                              <td style={{ fontSize: 12 }}>{log.entity_ref || "—"}</td>
                              <td style={{ fontSize: 11, color: "#555" }}>
                                <strong>{log.method}</strong>
                                <br />
                                <span style={{ wordBreak: "break-all" }}>{log.route}</span>
                              </td>
                              <td style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                                <span
                                  style={{
                                    backgroundColor: outcome.backgroundColor,
                                    color: outcome.color,
                                    borderRadius: 4,
                                    padding: "3px 8px",
                                    fontWeight: 600,
                                  }}
                                >
                                  {outcome.label}
                                </span>
                                <br />
                                <small style={{ color: "#666" }}>
                                  HTTP {log.status_code} · {log.duration_ms} ms
                                </small>
                              </td>
                              <td>
                                <Button color="link" size="sm" onClick={() => setSelected(log)}>
                                  View
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                <Row className="align-items-center">
                  <Col md={4} className="d-flex align-items-center" style={{ gap: 8 }}>
                    <span style={{ fontSize: 13 }}>Rows per page</span>
                    <Input
                      type="select"
                      bsSize="sm"
                      style={{ width: 90 }}
                      value={limit}
                      onChange={(e) => {
                        setLimit(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      {PAGE_SIZES.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={8} className="d-flex justify-content-end align-items-center" style={{ gap: 8 }}>
                    <span style={{ fontSize: 13, color: "#666" }}>
                      Page {pagination.page || 1} of {pagination.totalPages || 1}
                    </span>
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      disabled={!pagination.hasPrevPage}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      disabled={!pagination.hasNextPage}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </Col>
                </Row>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        {detailModal}
      </MDBox>
    </DashboardLayout>
  );
}
